import { Fragment, useState } from 'react';

import { ChevronRightIcon } from '@heroicons/react/20/solid';
import ReactECharts from 'echarts-for-react';

import { SectionTitle } from '@/components/common/SectionTitle';
import { fieldLabel, fieldMeta } from '@/features/decisions/config/fieldLabels';
import type {
  HistoricalPerformanceMetric,
  HistoricalPerformanceSeries,
  PeriodPoint,
} from '@/features/decisions/decisions.api.types';
import { buildMetricChartOption } from '@/features/decisions/utils/historicalPerformanceChartOption';
import {
  backtestSeriesColor,
  fmtMetricValue,
  policyLabel,
} from '@/features/decisions/utils/historicalPerformanceFormat';

interface Props {
  metrics: HistoricalPerformanceMetric[];
  refExperimentId: string;
  // Comparison policies in display order (drives column order after the reference).
  experimentIds: string[];
  optimizedExperimentId: string | null;
  netFcfUnlock: number | null;
  periodFrom: string;
  periodTo: string;
  periodCount: number;
  isLoading: boolean;
  error: Error | null;
}

// Per-metric improvement direction. The API does not yet carry this, so it is
// keyed by metric.key here; unknown metrics are treated as neutral (no good/bad
// coloring). TODO: have historical-performance return a `direction` per metric.
const METRIC_DIRECTION: Record<string, 'higher' | 'lower' | 'neutral'> = {
  hist_demand: 'neutral',
  model_target_inv_pos: 'lower',
  fill_rate: 'higher',
  model_ending_onhand_value: 'lower',
  fcf_cumulative_vs_ref: 'higher',
};

// Metrics hidden from the backtest table per product (bug bash) — noisy /
// duplicative rows. Keyed by the backend metric key.
const HIDDEN_METRIC_KEYS = new Set<string>([
  'fill_rate_beta_qty', // Fill rate (units)
  'available_in_full_per_review_cycle', // Review Cycle Available in Full
  'days_of_supply', // Days of Supply
]);

// Inline SVG sparkline of a series' period values.
function Sparkline({ points, color }: { points: PeriodPoint[]; color: string }) {
  const values = points.map((p) => p.value).filter((v): v is number => v != null);
  if (values.length < 2) return <span className="inline-block h-[18px] w-16" />;
  const w = 64;
  const h = 18;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const d = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * w;
      const y = h - ((v - min) / span) * h;
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg width={w} height={h} className="shrink-0" aria-hidden="true">
      <path d={d} fill="none" stroke={color} strokeWidth={1.25} />
    </svg>
  );
}

function fmtDelta(
  delta: number | null,
  unit: string,
  incumbentAggregate: number | null,
  direction: 'higher' | 'lower' | 'neutral',
): { text: string; tone: 'good' | 'bad' | 'neutral' } {
  if (delta == null || delta === 0) return { text: '—', tone: 'neutral' };

  const sign = delta > 0 ? '+' : '';
  let text: string;
  if (unit === 'ratio') {
    text = `${sign}${(delta * 100).toFixed(1)} pp`;
  } else {
    const base = `${sign}${fmtMetricValue(delta, unit)}`;
    const pct =
      incumbentAggregate && incumbentAggregate !== 0 ? Math.round((delta / Math.abs(incumbentAggregate)) * 100) : null;
    text = pct != null ? `${base} (${pct > 0 ? '+' : ''}${pct}%)` : base;
  }

  const improved = direction === 'neutral' ? null : direction === 'higher' ? delta > 0 : delta < 0;
  return { text, tone: improved == null ? 'neutral' : improved ? 'good' : 'bad' };
}

// Historical Performance table (TR-OR-01, issue #86): one row per backtest metric,
// a sparkline + aggregate per scenario, and the optimized policy's delta vs incumbent.
export function HistoricalPerformanceTable({
  metrics: allMetrics,
  refExperimentId,
  experimentIds,
  optimizedExperimentId,
  netFcfUnlock,
  periodFrom,
  periodTo,
  periodCount,
  isLoading,
  error,
}: Props) {
  const metrics = allMetrics.filter((m) => !HIDDEN_METRIC_KEYS.has(m.key));
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const totalCols = 2 + (metrics[0]?.series.length ?? 0);

  // Column order: incumbent (reference) first, then comparisons in the configured
  // experimentIds order (matches the Active Recommendation policy cards).
  const columns: HistoricalPerformanceSeries[] = (() => {
    const first = metrics[0]?.series ?? [];
    const ref = first.filter((s) => s.is_reference);
    const order = new Map(experimentIds.map((id, i) => [id, i]));
    const cmp = first
      .filter((s) => !s.is_reference)
      .sort((a, b) => (order.get(a.experiment_id) ?? Infinity) - (order.get(b.experiment_id) ?? Infinity));
    return [...ref, ...cmp];
  })();
  const optimizedId =
    optimizedExperimentId ?? columns.filter((c) => !c.is_reference).slice(-1)[0]?.experiment_id ?? null;

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="px-4 py-4">
        <SectionTitle>Historical Performance over the last {periodCount} weeks</SectionTitle>
        <p className="mt-0.5 text-xs text-gray-500">
          Backtest: each scenario replayed against actuals · {periodFrom} – {periodTo}
        </p>
      </div>

      {/* Only show the loading/empty/error placeholders when there is no data yet.
          Once metrics exist, keep the table mounted through background refetches so
          expanded rows (local state) survive a window-focus refetch. */}
      {metrics.length === 0 && isLoading && (
        <div className="px-4 py-6 text-sm text-gray-600">Loading historical performance…</div>
      )}
      {metrics.length === 0 && error && (
        <div className="m-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          <span className="font-medium">/historical-performance failed:</span> {error.message}
        </div>
      )}
      {metrics.length === 0 && !isLoading && !error && (
        <div className="px-4 py-6 text-sm text-gray-500">No historical performance for this SKU × site.</div>
      )}

      {metrics.length > 0 && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-y border-gray-200 text-xs text-gray-500">
                  <th className="px-4 py-2 text-left font-medium">Metric</th>
                  {columns.map((c) => (
                    <th key={c.experiment_id} className="px-4 py-2 text-right font-medium">
                      <span className="inline-flex items-center gap-1.5">
                        <span
                          className="inline-block h-2 w-2 rounded-full"
                          style={{ backgroundColor: backtestSeriesColor(c.experiment_id, c.is_reference) }}
                        />
                        {policyLabel(c)}
                      </span>
                    </th>
                  ))}
                  <th className="px-4 py-2 text-right font-medium">
                    Δ vs Incumbent
                    <div className="text-[10px] font-normal text-gray-400">Optimized</div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {metrics.map((m) => {
                  const byExp = new Map(m.series.map((s) => [s.experiment_id, s]));
                  const incumbent = byExp.get(refExperimentId) ?? m.series.find((s) => s.is_reference);
                  const optimized = optimizedId ? byExp.get(optimizedId) : undefined;
                  const direction = METRIC_DIRECTION[m.key] ?? 'neutral';
                  const delta = fmtDelta(
                    optimized?.delta_vs_ref ?? null,
                    m.unit,
                    incumbent?.aggregate ?? null,
                    direction,
                  );
                  const cadence = m.aggregation === 'CUM' ? 'CUMULATIVE' : 'WEEKLY';
                  // Prefer the central business label; fall back to the backend metric label.
                  const metricLabel = fieldMeta(m.key, 'backtest') ? fieldLabel(m.key, 'backtest') : m.label;
                  const toneClass =
                    delta.tone === 'good'
                      ? 'text-emerald-600'
                      : delta.tone === 'bad'
                        ? 'text-rose-600'
                        : 'text-gray-400';
                  const isOpen = expanded.has(m.key);
                  return (
                    <Fragment key={m.key}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3 align-top">
                          <button
                            type="button"
                            onClick={() => toggle(m.key)}
                            aria-expanded={isOpen}
                            className="flex items-start gap-2 text-left"
                          >
                            <ChevronRightIcon
                              className={`mt-0.5 h-4 w-4 shrink-0 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                            />
                            <span>
                              <span className="font-medium text-gray-900">{metricLabel}</span>
                              <span className="block text-[10px] uppercase tracking-wide text-gray-400">
                                {m.aggregation} · {cadence}
                              </span>
                            </span>
                          </button>
                        </td>
                        {columns.map((c) => {
                          const s = byExp.get(c.experiment_id);
                          return (
                            <td key={c.experiment_id} className="px-4 py-3">
                              <div className="flex items-center justify-end gap-2">
                                <Sparkline
                                  points={s?.period ?? []}
                                  color={backtestSeriesColor(c.experiment_id, c.is_reference)}
                                />
                                <span className="tabular-nums text-gray-900">
                                  {fmtMetricValue(s?.aggregate, m.unit)}
                                </span>
                              </div>
                            </td>
                          );
                        })}
                        <td className={`px-4 py-3 text-right font-semibold tabular-nums ${toneClass}`}>{delta.text}</td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan={totalCols} className="bg-gray-50/60 px-4 py-3">
                            <ReactECharts
                              opts={{ renderer: 'svg' }}
                              option={buildMetricChartOption(m)}
                              style={{ height: 260 }}
                              notMerge
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 bg-gray-50 px-4 py-3 text-xs text-gray-500">
            <span>
              Aggregation per row: total units for demand, avg for reorder point &amp; service level &amp; inventory,
              cumulative endpoint for FCF.
            </span>
            <span className="font-medium text-gray-700">
              Net FCF unlock @ Optimized: {fmtMetricValue(netFcfUnlock, 'usd')}
            </span>
          </div>
        </>
      )}
    </div>
  );
}
